import React, { useState, useEffect } from 'react';
import { Store, CreditCard, Home as HomeIcon, Car, Clock, UserRound, Building2, BanknoteIcon, TrendingUp, ShieldCheck, Zap, ArrowRight, Calculator, DollarSign, CalendarDays, ChevronDown, Pencil, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import BankComparison from '../components/BankComparison';
import PreferredBanks from '../components/PreferredBanks';
import FAQ from '../components/FAQ';
import Newsletter from '../components/Newsletter';
import LoanOnboarding from '../components/LoanOnboarding';
import AdContainer from '../components/AdContainer';
import { loanTypes } from '../data/loanData';
import { getPageBySlug, getPageComponents } from '../lib/pageService';

const creditOptions = [
  { 
    icon: <Store size={32} className="text-[#F7BA45]" />, 
    title: 'Kobi Kredisi',
    path: '/kobi-kredisi'
  },
  { 
    icon: <CreditCard size={32} className="text-[#F7BA45]" />, 
    title: 'İhtiyaç Kredisi',
    path: '/ihtiyac-kredisi'
  },
  { 
    icon: <HomeIcon size={32} className="text-[#F7BA45]" />, 
    title: 'Konut Kredisi',
    path: '/konut-kredisi'
  },
  { 
    icon: <Car size={32} className="text-[#F7BA45]" />, 
    title: 'Taşıt Kredisi',
    path: '/tasit-kredisi'
  },
  { 
    icon: <Clock size={32} className="text-[#F7BA45]" />, 
    title: 'Mevduat Faizi',
    path: '/mevduat'
  },
  { 
    icon: <UserRound size={32} className="text-[#F7BA45]" />, 
    title: 'Müşteri Ol',
    path: '/uzaktan-musteri-edinimi'
  },
  { 
    icon: <CreditCard size={32} className="text-[#F7BA45]" />, 
    title: 'Kredi Kartları',
    path: '/kredi-kartlari'
  },
  { 
    icon: <Building2 size={32} className="text-[#F7BA45]" />, 
    title: 'Ticari Kartlar',
    path: '/kredi-kartlari?type=ticari'
  },
  { 
    icon: <BanknoteIcon size={32} className="text-[#F7BA45]" />, 
    title: 'Faizsiz Kredi',
    path: '/ihtiyac-kredisi'
  }
];

const features = [
  {
    icon: <TrendingUp size={32} className="text-[#F7BA45]" />,
    title: "En Güncel Oranlar",
    description: "Tüm bankaların güncel faiz oranlarını anlık olarak karşılaştırın"
  },
  {
    icon: <ShieldCheck size={32} className="text-[#F7BA45]" />,
    title: "Güvenli Başvuru",
    description: "SSL sertifikalı güvenli altyapı ile başvurunuzu hemen yapın"
  },
  {
    icon: <Zap size={32} className="text-[#F7BA45]" />,
    title: "Hızlı Sonuç",
    description: "Kredi başvurunuz anında değerlendirilir ve sonuçlandırılır"
  }
];

// Default FAQs with all categories
const defaultFaqs = [
  {
    question: "Kredi başvurusu için hangi belgeler gereklidir?",
    answer: "Kredi başvurusu için temel olarak şu belgeler gereklidir:\n\n• Kimlik belgesi (Nüfus cüzdanı, ehliyet veya pasaport)\n• Gelir belgesi (Maaş bordrosu veya serbest çalışanlar için gelir beyanı)\n• İkametgah belgesi\n• Son 3 aylık banka hesap özeti\n\nBazı bankalar ek belgeler talep edebilir. Başvuru öncesi seçtiğiniz bankanın gereksinimlerini kontrol etmenizi öneririz.",
    category: "Kredi Başvurusu"
  },
  {
    question: "Online kredi başvurusu ne kadar sürede sonuçlanır?",
    answer: "Online kredi başvuruları genellikle çok hızlı değerlendirilir:\n\n• İhtiyaç kredisi: 5-10 dakika\n• Konut kredisi: 24-48 saat\n• Taşıt kredisi: 1-2 saat\n\nBaşvurunuzun hızlı sonuçlanması için tüm belgelerin eksiksiz ve doğru olması önemlidir.",
    category: "Kredi Başvurusu"
  },
  {
    question: "Kredi faiz oranları nasıl belirlenir?",
    answer: "Kredi faiz oranları şu faktörlere göre belirlenir:\n\n• TCMB politika faizi\n• Kredi türü (ihtiyaç, konut, taşıt)\n• Vade süresi\n• Kredi tutarı\n• Müşteri kredi notu\n• Bankanın risk politikaları\n\nKrediPark üzerinden tüm bankaların güncel faiz oranlarını karşılaştırabilirsiniz.",
    category: "Kredi Hesaplama"
  },
  {
    question: "Kredi taksit tutarı nasıl hesaplanır?",
    answer: "Kredi taksit tutarı şu bileşenleri içerir:\n\n• Ana para\n• Faiz tutarı\n• KKDF (Kaynak Kullanımı Destekleme Fonu)\n• BSMV (Banka Sigorta Muamele Vergisi)\n\nKrediPark'ın kredi hesaplama aracı ile farklı vade ve tutarlara göre ödeme planınızı kolayca oluşturabilirsiniz.",
    category: "Kredi Hesaplama"
  },
  {
    question: "Kredi notum düşükse kredi çekebilir miyim?",
    answer: "Düşük kredi notuna sahip kişiler için çeşitli alternatifler mevcuttur:\n\n• Kefil göstererek kredi başvurusu\n• İpotek karşılığı kredi\n• Düşük kredi notlular için özel kredi paketleri\n• Teminatlı kredi seçenekleri\n\nKrediPark, kredi notunuza uygun en avantajlı seçenekleri sunmaktadır.",
    category: "Kredi Notu"
  },
  {
    question: "Kredi notumu nasıl yükseltebilirim?",
    answer: "Kredi notunuzu yükseltmek için uygulayabileceğiniz stratejiler:\n\n• Kredi kartı ve kredi ödemelerinizi zamanında yapın\n• Kredi kartı limitinizin %30'undan fazlasını kullanmayın\n• Gereksiz kredi başvurularından kaçının\n• Düzenli gelir akışınızı koruyun\n• Çeşitli kredi ürünlerini kontrollü kullanın",
    category: "Kredi Notu"
  },
  {
    question: "Kredi erken kapatma durumunda ceza ödenir mi?",
    answer: "Kredi erken kapatma işlemlerinde:\n\n• Tüketici kredilerinde yasal sınırlamalar mevcuttur\n• Kalan vadeye göre hesaplanan makul bir erken ödeme ücreti alınabilir\n• Erken kapatma ücreti maksimum oranları yasa ile belirlenmiştir\n• Konut kredilerinde erken ödeme cezası daha düşüktür\n\nErken ödeme yapmadan önce bankanızdan detaylı bilgi almanızı öneririz.",
    category: "Ödemeler ve Vadeler"
  },
  {
    question: "Taksit ödemelerimi nasıl yapabilirim?",
    answer: "Kredi taksit ödemelerinizi şu kanallardan yapabilirsiniz:\n\n• İnternet bankacılığı\n• Mobil bankacılık\n• ATM'ler\n• Banka şubeleri\n• Otomatik ödeme talimatı\n\nÖdeme tarihinden önce hesabınızda yeterli bakiye bulundurmanız önemlidir.",
    category: "Ödemeler ve Vadeler"
  },
  {
    question: "Emekliler hangi kredi avantajlarından yararlanabilir?",
    answer: "Emekliler için özel kredi avantajları:\n\n• Düşük faiz oranları\n• Esnek ödeme seçenekleri\n• Masrafsız kredi imkanları\n• Yüksek kredi limitleri\n• Özel sigorta paketleri\n\nKrediPark üzerinden emeklilere özel kampanyaları inceleyebilirsiniz.",
    category: "Özel Durumlar"
  },
  {
    question: "Yapılandırma veya refinansman kredisi nedir?",
    answer: "Yapılandırma kredisi mevcut kredilerinizi tek bir kredide toplama imkanı sunar:\n\n• Daha düşük faiz oranı\n• Tek ve düzenli ödeme planı\n• Uzun vade seçeneği\n• Aylık taksit tutarında azalma\n• Borç yönetiminde kolaylık\n\nMevcut kredi borçlarınızı yapılandırmak için bankaların özel tekliflerini karşılaştırabilirsiniz.",
    category: "Özel Durumlar"
  }
];

const HomePage = () => {
  const [selectedLoanType, setSelectedLoanType] = useState(loanTypes[0]);
  const [amount, setAmount] = useState(selectedLoanType.minAmount);
  const [isManualInput, setIsManualInput] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [term, setTerm] = useState(12);
  const [hoveredType, setHoveredType] = useState<string | null>(null);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pageContent, setPageContent] = useState<any>(null);
  const [customFaqs, setCustomFaqs] = useState<any[]>([]);
  const [customBankLogos, setCustomBankLogos] = useState<any[]>([]);
  const [pageComponents, setPageComponents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        setIsLoading(true);
        const page = await getPageBySlug('anasayfa');
        if (page && page.content) {
          try {
            let content;
            if (typeof page.content === 'string') {
              content = JSON.parse(page.content);
            } else {
              content = page.content;
            }
            
            setPageContent(content);
            
            // Set custom FAQs if available
            if (content.faqs && Array.isArray(content.faqs)) {
              setCustomFaqs(content.faqs);
            }
            
            // Set custom bank logos if available
            if (content.bankLogos && Array.isArray(content.bankLogos)) {
              setCustomBankLogos(content.bankLogos);
            }
            
            // Get page components
            if (content.components && Array.isArray(content.components)) {
              setPageComponents(content.components);
            } else {
              // Try to fetch components separately
              const components = await getPageComponents('anasayfa');
              setPageComponents(components);
            }
          } catch (error) {
            console.error('Error parsing page content:', error);
          }
        }
      } catch (error) {
        console.error('Error fetching page content:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPageContent();
  }, []);

  useEffect(() => {
    setAmount(selectedLoanType.minAmount);
    if (selectedLoanType.id === 'ihtiyac') {
      const maxTerm = selectedLoanType.getMaxTerm(amount);
      setTerm(Math.min(term, maxTerm));
    } else if (selectedLoanType.id === 'tasit') {
      const maxTerm = selectedLoanType.getMaxTerm(amount);
      setTerm(Math.min(term, maxTerm));
    } else {
      setTerm(selectedLoanType.minTerm);
    }
  }, [selectedLoanType]);

  useEffect(() => {
    if (selectedLoanType.id === 'ihtiyac') {
      const maxTerm = selectedLoanType.getMaxTerm(amount);
      if (term > maxTerm) {
        setTerm(maxTerm);
      }
    } else if (selectedLoanType.id === 'tasit') {
      const maxTerm = selectedLoanType.getMaxTerm(amount);
      if (term > maxTerm) {
        setTerm(maxTerm);
      }
    }
  }, [amount, selectedLoanType.id]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.round(parseInt(e.target.value) / selectedLoanType.step) * selectedLoanType.step;
    if (value >= selectedLoanType.minAmount && value <= selectedLoanType.maxAmount) {
      setAmount(value);
    }
  };

  const formatNumber = (value: string) => {
    const num = value.replace(/\D/g, '');
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleManualAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const formattedValue = formatNumber(rawValue);
    setManualAmount(formattedValue);
    
    const numValue = parseInt(rawValue);
    if (!isNaN(numValue) && numValue >= selectedLoanType.minAmount && numValue <= selectedLoanType.maxAmount) {
      // Round to nearest step value
      const roundedValue = Math.round(numValue / selectedLoanType.step) * selectedLoanType.step;
      setAmount(roundedValue);
    }
  };

  const handleConfirmManualInput = () => {
    const numValue = parseInt(manualAmount.replace(/\./g, ''));
    if (!isNaN(numValue) && numValue >= selectedLoanType.minAmount && numValue <= selectedLoanType.maxAmount) {
      // Round to nearest step value
      const roundedValue = Math.round(numValue / selectedLoanType.step) * selectedLoanType.step;
      setAmount(roundedValue);
      setIsManualInput(false);
    }
  };

  const toggleManualInput = () => {
    setIsManualInput(!isManualInput);
    if (!isManualInput) {
      setManualAmount(formatNumber(amount.toString()));
    }
  };

  const getAvailableTerms = () => {
    if (selectedLoanType.id === 'ihtiyac') {
      if (amount < 125000) return selectedLoanType.terms.low;
      if (amount <= 250000) return selectedLoanType.terms.medium;
      return selectedLoanType.terms.high;
    } else if (selectedLoanType.id === 'tasit') {
      if (amount <= 2500000) return selectedLoanType.terms.level1;
      if (amount <= 5000000) return selectedLoanType.terms.level2;
      if (amount <= 6500000) return selectedLoanType.terms.level3;
      if (amount <= 7500000) return selectedLoanType.terms.level4;
      return selectedLoanType.terms.level5;
    } else {
      return [
        selectedLoanType.minTerm,
        selectedLoanType.minTerm + Math.round((selectedLoanType.maxTerm - selectedLoanType.minTerm) * 0.33),
        selectedLoanType.minTerm + Math.round((selectedLoanType.maxTerm - selectedLoanType.minTerm) * 0.66),
        selectedLoanType.maxTerm
      ];
    }
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      const millions = value / 1000000;
      return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)} M`;
    }
    return new Intl.NumberFormat('tr-TR', {
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleCalculate = () => {
    setIsOnboardingOpen(true);
  };

  // Prepare FAQs for the component
  const faqsToDisplay = customFaqs && customFaqs.length > 0 
    ? customFaqs 
    : defaultFaqs;

  // Render a component based on its type
  const renderComponent = (component: any) => {
    switch (component.type) {
      case 'faq':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Sık Sorulan Sorular"}
                  </h2>
                  <p className="text-slate-600 mb-12">
                    {component.settings?.subtitle || "Merak ettiğiniz tüm sorular ve cevapları"}
                  </p>
                </div>
                <FAQ customFaqs={component.content?.faqs || faqsToDisplay} />
              </div>
            </div>
          </div>
        );
      case 'bankList':
        return (
          <div key={component.id} className="py-16 bg-slate-50">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Birlikte Çalıştığımız Bankalar"}
                  </h2>
                  <p className="text-slate-600">
                    {component.settings?.subtitle || "Kredipark.com'da sadece avantajlı teklifler sunan bankaların ürünleri ve teklifleri yer almaktadır."}
                  </p>
                </div>
                <PreferredBanks customBanks={component.content?.bankLogos || customBankLogos} />
              </div>
            </div>
          </div>
        );
      case 'newsletter':
        return <Newsletter key={component.id} />;
      case 'bankComparison':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {component.settings?.title || "Banka Karşılaştırma"}
                  </h2>
                  <p className="text-slate-600 mb-12">
                    {component.settings?.subtitle || "Bankaları karşılaştırın, size en uygun olanı seçin"}
                  </p>
                </div>
                <BankComparison />
              </div>
            </div>
          </div>
        );
      case 'textBlock':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                {component.settings?.showTitle && (
                  <h2 className="text-3xl font-bold mb-6 text-slate-800 text-center">
                    {component.settings?.title || "Metin Bloğu"}
                  </h2>
                )}
                <div 
                  className={`prose prose-slate max-w-none text-${component.settings?.textAlign || 'left'}`}
                  dangerouslySetInnerHTML={{ __html: component.content?.text || '<p>Metin içeriği</p>' }}
                />
              </div>
            </div>
          </div>
        );
      case 'imageBlock':
        return (
          <div key={component.id} className="py-16 bg-slate-50">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                {component.settings?.showTitle && (
                  <h2 className="text-3xl font-bold mb-6 text-slate-800 text-center">
                    {component.settings?.title || "Görsel Bloğu"}
                  </h2>
                )}
                <div className={`text-${component.settings?.imageAlign || 'center'}`}>
                  <img 
                    src={component.content?.imageUrl || 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&q=80&w=1200&h=600'} 
                    alt={component.content?.caption || 'Görsel'} 
                    className="rounded-lg max-w-full mx-auto"
                  />
                  {component.settings?.showCaption && component.content?.caption && (
                    <p className="text-sm text-slate-500 mt-2">{component.content.caption}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      case 'callToAction':
        return (
          <div key={component.id} className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div 
                  className="text-center p-12 rounded-2xl shadow-lg"
                  style={{ backgroundColor: component.settings?.backgroundColor || '#ffffff' }}
                >
                  <h2 
                    className="text-3xl font-bold mb-4"
                    style={{ color: component.settings?.textColor || '#1e293b' }}
                  >
                    {component.content?.heading || 'Aksiyon Çağrısı Başlığı'}
                  </h2>
                  <p 
                    className="text-lg mb-8"
                    style={{ color: component.settings?.textColor || '#1e293b' }}
                  >
                    {component.content?.description || 'Kısa açıklama metni'}
                  </p>
                  <a 
                    href={component.content?.buttonUrl || '#'}
                    className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
                    style={{ backgroundColor: component.settings?.buttonColor || '#F7BA45', color: '#1e293b' }}
                  >
                    {component.settings?.buttonText || 'Hemen Başvur'}
                    <ArrowRight size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-16 h-16 border-4 border-[#F7BA45] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      {/* Masthead Ad */}
      <AdContainer location="masthead" />

      <div className="relative bg-gradient-to-b from-slate-200 to-slate-100 py-8 md:py-16 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -left-4 -top-4 w-72 h-72 bg-[#F7BA45]/30 rounded-full mix-blend-multiply blur-xl animate-blob" />
          <div className="absolute -right-4 -top-4 w-72 h-72 bg-pink-300/30 rounded-full mix-blend-multiply blur-xl animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-300/30 rounded-full mix-blend-multiply blur-xl animate-blob animation-delay-4000" />
        </div>

        <main className="container mx-auto px-4 relative">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1 order-2 lg:order-1">
              <div className="max-w-xl">
                <h1 className="hidden lg:block text-4xl md:text-5xl font-bold mb-6 text-slate-800 leading-tight">
                  {pageContent?.heroTitle || "Bankaları Karşılaştır, En Uygun Olana Başvur"}
                </h1>
                <p className="hidden lg:block text-lg text-slate-600 mb-8">
                  {pageContent?.heroSubtitle || "Türkiye'nin en kapsamlı banka ve kredi karşılaştırma platformu"}
                </p>

                <div className="grid grid-cols-3 gap-3">
                  {(pageContent?.creditOptions || creditOptions).slice(0, 6).map((option: any, index: number) => {
                    // Determine which icon to use based on iconName
                    let IconComponent;
                    if (pageContent?.creditOptions) {
                      switch (option.iconName) {
                        case 'Store': IconComponent = <Store size={32} className="text-[#F7BA45]" />; break;
                        case 'CreditCard': IconComponent = <CreditCard size={32} className="text-[#F7BA45]" />; break;
                        case 'Home': IconComponent = <HomeIcon size={32} className="text-[#F7BA45]" />; break;
                        case 'Car': IconComponent = <Car size={32} className="text-[#F7BA45]" />; break;
                        case 'Clock': IconComponent = <Clock size={32} className="text-[#F7BA45]" />; break;
                        case 'UserRound': IconComponent = <UserRound size={32} className="text-[#F7BA45]" />; break;
                        case 'Building2': IconComponent = <Building2 size={32} className="text-[#F7BA45]" />; break;
                        case 'BanknoteIcon': IconComponent = <BanknoteIcon size={32} className="text-[#F7BA45]" />; break;
                        default: IconComponent = <CreditCard size={32} className="text-[#F7BA45]" />;
                      }
                    } else {
                      IconComponent = option.icon;
                    }
                    
                    return (
                      <Link 
                        key={index}
                        to={option.path}
                        className="bg-white/80 p-3 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer border border-slate-200/50 hover:border-[#F7BA45]/50 group"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <div className="bg-slate-100 p-2 rounded-full group-hover:bg-[#F7BA45]/10 transition-colors">
                            {IconComponent}
                          </div>
                          <span className="text-sm font-medium text-slate-700 text-center group-hover:text-slate-900">
                            {option.title}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="lg:w-[450px] w-full order-1 lg:order-2">
              {/* Mobile Title and Description - Only visible on mobile */}
              <div className="lg:hidden text-center mb-6">
                <h1 className="text-2xl font-bold mb-4 text-slate-800 leading-tight">
                  {pageContent?.heroTitle || "Bankaları Karşılaştır, En Uygun Olana Başvur"}
                </h1>
                <p className="text-base text-slate-600">
                  Türkiye'nin en kapsamlı banka karşılaştırma platformu
                </p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200 relative">
                <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-transparent rounded-2xl backdrop-blur-sm -z-10" />
                
                <div className="flex items-center gap-3 mb-8">
                  <div className="bg-[#F7BA45]/10 p-2 rounded-lg">
                    <Calculator size={24} className="text-[#F7BA45]" />
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 text-xl font-semibold text-slate-800 hover:text-slate-900 transition-colors"
                    >
                      {selectedLoanType.title}
                      <ChevronDown 
                        size={20} 
                        className={`transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50">
                        {loanTypes.map((type) => (
                          <button
                            key={type.id}
                            onClick={() => {
                              setSelectedLoanType(type);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors ${
                              selectedLoanType.id === type.id ? 'text-[#F7BA45] font-medium' : 'text-slate-600'
                            }`}
                          >
                            {type.icon}
                            <span>{type.title}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <span className="text-lg font-semibold text-[#F7BA45]">₺</span>
                        Kredi Tutarı
                      </label>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={toggleManualInput}
                          className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <Pencil size={16} className="text-slate-400 hover:text-[#F7BA45]" />
                        </button>
                        {isManualInput ? (
                          <div className="flex items-center">
                            <div className="relative flex items-center">
                              <input
                                type="text"
                                value={manualAmount}
                                onChange={handleManualAmountChange}
                               readOnly={/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)}
                                className="w-32 text-right border-b-2 border-[#F7BA45] focus:outline-none pr-2"
                                autoFocus
                              />
                              <span className="ml-1">TL</span>
                              <button
                                onClick={handleConfirmManualInput}
                                className="ml-2 p-1.5 hover:bg-[#F7BA45]/10 rounded-lg transition-colors"
                              >
                                <Check size={16} className="text-[#F7BA45]" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-lg font-semibold text-slate-800">
                            {amount.toLocaleString('tr-TR')} TL
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min={selectedLoanType.minAmount}
                        max={selectedLoanType.maxAmount}
                        step={selectedLoanType.step}
                        value={amount}
                        onChange={handleAmountChange}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="grid grid-cols-5 gap-2 mt-4">
                        {selectedLoanType.amounts.map((point) => (
                          <button
                            key={point}
                            onClick={() => setAmount(point)}
                            className={`h-10 flex items-center justify-center text-sm rounded transition-all ${
                              Math.abs(amount - point) < selectedLoanType.step
                                ? 'bg-[#F7BA45] text-white shadow-md'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {formatCurrency(point)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <CalendarDays size={16} strokeWidth={2.5} className="text-[#F7BA45]" />
                        Vade Süresi
                      </label>
                      <span className="text-lg font-semibold text-slate-800">
                        {term} Ay
                      </span>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min={selectedLoanType.id === 'ihtiyac' || selectedLoanType.id === 'tasit' ? Math.min(...getAvailableTerms()) : selectedLoanType.minTerm}
                        max={selectedLoanType.id === 'ihtiyac' || selectedLoanType.id === 'tasit' ? Math.max(...getAvailableTerms()) : selectedLoanType.maxTerm}
                        value={term}
                        onChange={(e) => setTerm(Number(e.target.value))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="grid grid-cols-6 gap-2 mt-4">
                        {getAvailableTerms().map((point) => (
                          <button
                            key={point}
                            onClick={() => setTerm(point)}
                            className={`py-2 px-4 text-center rounded transition-all min-w-[60px] ${
                              term === point
                                ? 'bg-[#F7BA45] text-white shadow-md'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            <span className="text-lg font-semibold">{point}</span>
                            <span className="text-xs">Ay</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button 
                    onClick={handleCalculate}
                    className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 font-medium py-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 group shadow-md hover:shadow-lg"
                  >
                    KREDİ HESAPLA
                    <ArrowRight className="transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Render page components */}
      {pageComponents.length > 0 ? (
        pageComponents
          .sort((a, b) => a.order - b.order)
          .map(component => renderComponent(component))
      ) : (
        <>
          <div className="py-16 bg-slate-50">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {pageContent?.banksTitle || "Birlikte Çalıştığımız Bankalar"}
                  </h2>
                  <p className="text-slate-600">
                    {pageContent?.banksSubtitle || "Kredipark.com'da sadece avantajlı teklifler sunan bankaların ürünleri ve teklifleri yer almaktadır."}
                  </p>
                </div>
                <PreferredBanks customBanks={customBankLogos} />
              </div>
            </div>
          </div>

          <div className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold mb-4 text-slate-800">
                    {pageContent?.faqTitle || "Sık Sorulan Sorular"}
                  </h2>
                  <p className="text-slate-600 mb-12">
                    {pageContent?.faqSubtitle || "Kredi başvurusu ve süreçleri hakkında merak ettiğiniz tüm sorular ve cevapları"}
                  </p>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#F7BA45] rounded-full"></div>
                        Kredi başvurusu için hangi belgeler gereklidir?
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Kredi başvurusu için temel olarak kimlik belgesi, gelir belgesi, ikametgah belgesi ve son 3 aylık banka hesap özeti gereklidir. Bazı bankalar ek belgeler talep edebilir.
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#F7BA45] rounded-full"></div>
                        Online kredi başvurusu ne kadar sürede sonuçlanır?
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        İhtiyaç kredisi 5-10 dakika, konut kredisi 24-48 saat, taşıt kredisi 1-2 saat içinde sonuçlanır. Başvurunuzun hızlı sonuçlanması için tüm belgelerin eksiksiz olması önemlidir.
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#F7BA45] rounded-full"></div>
                        Kredi faiz oranları nasıl belirlenir?
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Kredi faiz oranları TCMB politika faizi, kredi türü, vade süresi, kredi tutarı, müşteri kredi notu ve bankanın risk politikalarına göre belirlenir.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#F7BA45] rounded-full"></div>
                        Kredi notum düşükse kredi çekebilir miyim?
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Düşük kredi notuna sahip kişiler için kefil göstererek kredi başvurusu, ipotek karşılığı kredi ve teminatlı kredi seçenekleri mevcuttur.
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#F7BA45] rounded-full"></div>
                        Kredi erken kapatma durumunda ceza ödenir mi?
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Tüketici kredilerinde yasal sınırlamalar mevcuttur. Kalan vadeye göre hesaplanan makul bir erken ödeme ücreti alınabilir. Konut kredilerinde erken ödeme cezası daha düşüktür.
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#F7BA45] rounded-full"></div>
                        Emekliler hangi kredi avantajlarından yararlanabilir?
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        Emekliler için düşük faiz oranları, esnek ödeme seçenekleri, masrafsız kredi imkanları, yüksek kredi limitleri ve özel sigorta paketleri mevcuttur.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Newsletter />
        </>
      )}

      <LoanOnboarding
        isOpen={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        loanAmount={amount}
        loanTerm={term}
      />
    </>
  );
};

export default HomePage;